/**
 * PatternInsights — Coach YUMIX d'analyse des habitudes (Yuri spec Iter. 327).
 *
 * Refonte complète : widget pliable avec menu déroulant 7 périodes (Jour,
 * Semaine, 2 semaines, Mois, 3 mois, 6 mois, 1 an). Style "Clean & Glow"
 * émeraude pour signifier "intelligence YUMIX" et se distinguer des autres
 * widgets (Or = Discipline, Bleu = Hydratation, Rouge = Victoires, Mauve =
 * Santé Féminine).
 */
import React, { useState, useEffect } from 'react';
import { useLanguage, api } from '../context/AppContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Brain,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Lightbulb,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import PillarDetailModal from './PillarDetailModal';

const PatternInsights = () => {
  const { language } = useLanguage();
  const fr = String(language || 'fr').startsWith('fr');
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('1m');
  const [totalMeals, setTotalMeals] = useState(0);
  const [open, setOpen] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  // Iter. 406.fr.4 — Pillar deep-dive modal
  const [activePillar, setActivePillar] = useState(null);

  const periods = [
    { value: '1d', label: { fr: 'Jour',         en: 'Today' } },
    { value: '1w', label: { fr: '1 semaine',    en: '1 week' } },
    { value: '2w', label: { fr: '2 semaines',   en: '2 weeks' } },
    { value: '1m', label: { fr: '1 mois',       en: '1 month' } },
    { value: '3m', label: { fr: '3 mois',       en: '3 months' } },
    { value: '6m', label: { fr: '6 mois',       en: '6 months' } },
    { value: '1y', label: { fr: '1 an',         en: '1 year' } },
  ];

  const fetchPatterns = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/insights/patterns?period=${period}`);
      setInsights(response.data.insights || []);
      setTotalMeals(response.data.total_meals_analyzed || 0);
      setHasFetched(true);
    } catch (e) {
      console.error('Patterns error:', e);
      toast.error(fr ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  // Iter. 406.fr.7 — fetch when accordion opens OR period changes.
  // Removed the `user` gate which caused a race: on cold logins useAuth().user
  // is null at the moment `open` toggles true, the effect skips, and React
  // never re-fires it because the user reference identity doesn't change
  // after the auth context resolves. fetchPatterns hits /insights/patterns
  // which is auth-gated by the session-token interceptor — no need to gate
  // on `user` client-side.
  useEffect(() => {
    if (open) fetchPatterns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, period]);

  const sevStyle = (s) => {
    if (s === 'positive') return 'bg-emerald-500/10 border-emerald-500/30';
    if (s === 'warning') return 'bg-orange-500/10 border-orange-500/30';
    return 'bg-secondary/40 border-border';
  };
  const sevIcon = (s) => {
    if (s === 'positive') return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />;
    if (s === 'warning') return <AlertTriangle className="w-3.5 h-3.5 text-orange-400" />;
    return <Lightbulb className="w-3.5 h-3.5 text-muted-foreground" />;
  };

  const periodLabel = (periods.find(p => p.value === period)?.label[fr ? 'fr' : 'en']) || '';

  return (
    <div
      data-testid="pattern-insights"
      className="rounded-2xl border border-emerald-400/40 bg-gradient-to-br from-emerald-950/40 via-card/60 to-teal-950/30 overflow-hidden"
      style={{
        boxShadow: '0 0 22px rgba(52,211,153,0.32), inset 0 0 0 1px rgba(52,211,153,0.12)',
      }}
    >
      {/* Trigger row */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="pattern-insights-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-emerald-500/5 active:scale-[0.99] transition-all"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 ring-1 ring-emerald-300/50 flex items-center justify-center"
            style={{ boxShadow: '0 0 14px rgba(52,211,153,0.55)' }}
          >
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div className="text-left min-w-0">
            <p className="font-heading text-sm font-bold uppercase tracking-[0.18em] text-emerald-200 truncate">
              {fr ? 'Analyse de tes Habitudes' : 'Habits Analysis'}
            </p>
            <p className="text-[11px] text-emerald-300/70 truncate">
              {fr ? `Coach YUMIX · ${periodLabel}` : `YUMIX Coach · ${periodLabel}`}
            </p>
          </div>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-emerald-300/80 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Body */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 border-t border-emerald-500/15">
              {/* Period selector */}
              <div className="flex items-center justify-between gap-2 my-3">
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger
                    data-testid="pattern-period-trigger"
                    className="w-36 h-9 bg-black/40 border-emerald-500/30 text-xs uppercase tracking-wider"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {periods.map(p => (
                      <SelectItem key={p.value} value={p.value} data-testid={`pattern-period-${p.value}`}>
                        {p.label[fr ? 'fr' : 'en']}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={fetchPatterns}
                  disabled={loading}
                  data-testid="pattern-refresh-btn"
                  className="h-9 w-9 rounded-md bg-black/40 border border-emerald-500/30 flex items-center justify-center hover:bg-emerald-500/10 transition-colors disabled:opacity-50"
                  aria-label={fr ? 'Rafraîchir' : 'Refresh'}
                >
                  <RefreshCw className={`w-4 h-4 text-emerald-300 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {hasFetched && (
                <p className="text-[11px] text-emerald-300/60 mb-2">
                  {fr ? `Basé sur ${totalMeals} repas analysés` : `Based on ${totalMeals} meals analyzed`}
                </p>
              )}

              {/* Insights list */}
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
                </div>
              ) : insights.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Brain className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{fr ? 'Aucun insight disponible' : 'No insights available'}</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  <AnimatePresence mode="popLayout">
                    {insights.map((it, i) => {
                      const pid = it.pillar_id || it.category;
                      const isPillar = pid && pid !== 'insufficient_data';
                      return (
                      <motion.button
                        type="button"
                        key={`${pid}-${i}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ delay: i * 0.04 }}
                        onClick={isPillar ? () => setActivePillar(pid) : undefined}
                        disabled={!isPillar}
                        className={`w-full text-left p-3 rounded-lg border ${sevStyle(it.severity)} ${isPillar ? 'cursor-pointer hover:border-emerald-400/60 hover:scale-[1.01] active:scale-[0.99]' : 'cursor-default'} transition-all`}
                        data-testid={`insight-${pid}`}
                        aria-label={isPillar ? (fr ? `Voir l'analyse détaillée de ${it.title}` : `View detailed analysis of ${it.title}`) : undefined}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-xl leading-none mt-0.5">{it.icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-foreground text-sm">{it.title}</h4>
                              {sevIcon(it.severity)}
                              {isPillar && <ChevronRight className="w-3.5 h-3.5 text-emerald-300/70 ml-auto shrink-0" />}
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {it.description}
                            </p>
                            {isPillar && (
                              <p className="text-[10px] text-emerald-300/80 mt-1.5 font-bold uppercase tracking-wider">
                                {fr ? 'Touche pour l\'analyse détaillée →' : 'Tap for detailed analysis →'}
                              </p>
                            )}
                          </div>
                        </div>
                      </motion.button>
                      );
                    })}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Iter. 406.fr.4 — Pillar deep-dive modal */}
      {activePillar && (
        <PillarDetailModal
          pillarId={activePillar}
          period={period}
          language={language}
          onClose={() => setActivePillar(null)}
        />
      )}
    </div>
  );
};

export default PatternInsights;
